import React from 'react';
import { StatsInfo } from '@components/Profile/Sections/components/StatsInfo';
import { LoadingSkeleton } from '@components/basics/LoadingSkeleton';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import blurImage from '@components/main-components/image/blur-image';
import { ChannelMembersModal } from '@components/modals/Channels/ChannelMembersModal';
import { ModalType, useModal } from '@context/modal';
import { ChannelInfoFragmentDoc, type FragmentType, getFragment, gql } from '@graphql/reactQuery';
import { useStableCallback } from '@hooks/useStableCallback';
import { useChannelSettingsPath } from '../helpers';
import { useChannelStats } from '../useChannelStats';
import { ChannelActionBar } from './ActionBar';
import { ChannelSocialProof } from './SocialProof';

const MOBILE_PICTURE_SIZE = 108;
const TABLET_PICTURE_SIZE = 144;

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ChannelInfo on Channel {
    id
    name
    description
    nameSlug
    profileImage {
      id
      url
    }
    telegramUrl
    tiktokUrl
    twitterUrl
    webappUri
    websiteUrl
    discordUrl
  }
`);

export function ChannelInfo({
  channelFrag,
}: {
  channelFrag: FragmentType<ChannelInfoFragmentDoc>;
}) {
  const channel = getFragment(ChannelInfoFragmentDoc, channelFrag);

  const profilePictureSrc = channel.profileImage?.url ?? '/images/sound_letter_s.svg';

  const { openModal } = useModal();

  const settingsPath = useChannelSettingsPath(channel.nameSlug);

  const { isLoading, joinStatus, postCount, memberCount, notificationStatus } = useChannelStats({
    channelId: channel.id,
  });

  const renderProfileImage = useStableCallback((size: number) => {
    return (
      <NextFutureImage
        src={profilePictureSrc}
        alt="Profile Image"
        width={size}
        height={size}
        className="h-full rounded-md object-cover"
        placeholder="blur"
        blurDataURL={blurImage(size, size)}
      />
    );
  });

  return (
    <div className="flex w-full flex-col gap-6 lt:px-0">
      <div className="relative flex w-full items-end justify-between lg:items-end">
        <div className="flex md:hidden">{renderProfileImage(MOBILE_PICTURE_SIZE)}</div>
        <div className="group z-above2 hidden md:flex">
          {renderProfileImage(TABLET_PICTURE_SIZE)}
        </div>

        <div className="block lg:hidden">
          <ChannelActionBar
            channelId={channel.id}
            channelWebappUri={channel.webappUri}
            settingsPath={settingsPath}
            loading={isLoading}
            joinStatus={joinStatus}
            notificationStatus={notificationStatus}
          />
        </div>
      </div>

      <div className="relative flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-1 md:gap-2">
          <div className="flex w-full items-center justify-between">
            <LoadingSkeleton
              loading={isLoading}
              className="mb-[6px] h-5 w-2/5 md:mb-[10px] md:h-6 md:w-1/4"
            >
              <div className="flex items-center gap-1">
                <h2 className="font-title text-title-l font-medium md:text-title-xl">
                  {channel.name}
                </h2>
              </div>
            </LoadingSkeleton>
            <div className="hidden lg:block">
              <ChannelActionBar
                channelId={channel.id}
                channelWebappUri={channel.webappUri}
                settingsPath={settingsPath}
                loading={isLoading}
                joinStatus={joinStatus}
                notificationStatus={notificationStatus}
              />
            </div>
          </div>

          <span className="mt-3 line-clamp-2 block font-base text-base-m text-base700 lg:hidden">
            {channel.description}
          </span>
        </div>

        <div className="flex gap-5 pt-1 md:gap-8">
          <StatsInfo loading={isLoading} total={postCount} label="Posts" />

          <StatsInfo
            loading={isLoading}
            total={memberCount}
            label="Members"
            onClick={() => {
              openModal(ModalType.CHANNEL_MEMBERS, {
                body: <ChannelMembersModal channelId={channel.id} membersNumber={memberCount} />,
              });
            }}
          />
        </div>

        <ChannelSocialProof channelId={channel.id} />
      </div>
    </div>
  );
}
